import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  useRenameArtifact,
  useDeleteArtifact,
} from '@/hooks/queries/artifacts/use-artifact-mutations';
import {
  refreshFileUrls,
  downloadFileWithName,
} from '@/services/workflow/get-file';
import { useOrgStore } from '@/stores/org-store';
import type { ArtifactListItem, ArtifactFile } from '@/types/artifacts';
import type { NodeFile } from '@/types/node-types';

export function artifactFileToNodeFile(file: ArtifactFile): NodeFile {
  return {
    id: file.id,
    name: file.name,
    mime_type: file.mime_type,
    s3_bucket: file.s3_bucket,
    s3_key: file.s3_key,
    s3_region: file.s3_region,
    generated: file.generated,
    presigned_url: file.presigned_url ?? '',
    download_url: file.download_url,
    proxy_url: file.proxy_url,
  };
}

export function listItemPrimaryToNodeFile(
  artifact: ArtifactListItem,
  override?: ArtifactFile,
): NodeFile | null {
  if (override) return artifactFileToNodeFile(override);

  if (!artifact.primary_file_s3_bucket || !artifact.primary_file_s3_key) {
    return null;
  }

  return {
    name: artifact.primary_file_name ?? artifact.title,
    mime_type: artifact.primary_file_mime_type ?? 'application/octet-stream',
    s3_bucket: artifact.primary_file_s3_bucket,
    s3_key: artifact.primary_file_s3_key,
    s3_region: artifact.primary_file_s3_region ?? '',
    generated: artifact.primary_file_generated ?? true,
    presigned_url: artifact.primary_presigned_url ?? '',
    download_url: artifact.primary_download_url,
    proxy_url: artifact.primary_proxy_url,
  };
}

export async function refreshNodeFile(
  orgId: string,
  file: NodeFile,
): Promise<NodeFile> {
  if (!file.s3_bucket || !file.s3_key) return file;
  try {
    const [refreshed] = await refreshFileUrls(orgId, [file]);
    return refreshed ?? file;
  } catch (err) {
    Sentry.captureException(err, {
      extra: { context: 'refresh-node-file', orgId, s3Key: file.s3_key },
    });
    return file;
  }
}

export function useArtifactActions(artifact: ArtifactListItem) {
  const { currentOrg } = useOrgStore();
  const renameMutation = useRenameArtifact();
  const deleteMutation = useDeleteArtifact();

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(artifact.title);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const openRenameDialog = () => {
    setRenameValue(artifact.title);
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    const trimmedValue = renameValue.trim();
    if (!trimmedValue || trimmedValue === artifact.title) {
      setRenameDialogOpen(false);
      return;
    }
    setRenameDialogOpen(false);
    try {
      await renameMutation.mutateAsync({
        artifactId: artifact.id,
        title: trimmedValue,
      });
    } catch (err) {
      setRenameDialogOpen(true);
      Sentry.captureException(err, {
        extra: { context: 'rename-artifact', artifactId: artifact.id },
      });
    }
  };

  const handleDelete = async (onSuccess?: () => void) => {
    try {
      await deleteMutation.mutateAsync(artifact.id);
      setDeleteDialogOpen(false);
      onSuccess?.();
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'delete-artifact', artifactId: artifact.id },
      });
    }
  };

  const handleDownload = async (file: NodeFile | null) => {
    if (!file || !currentOrg?.orgid || isDownloading) return;
    setIsDownloading(true);
    try {
      const fileToDownload = await refreshNodeFile(currentOrg.orgid, file);
      const downloadUrl =
        fileToDownload.download_url || fileToDownload.presigned_url;
      if (!downloadUrl) throw new Error('No download URL available');
      await downloadFileWithName(downloadUrl, file.name);
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'download-artifact-file', artifactId: artifact.id },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    renameDialogOpen,
    setRenameDialogOpen,
    renameValue,
    setRenameValue,
    deleteDialogOpen,
    setDeleteDialogOpen,
    isDownloading,
    openRenameDialog,
    handleRename,
    handleDelete,
    handleDownload,
    renamePending: renameMutation.isPending,
    deletePending: deleteMutation.isPending,
  };
}
